import type { AvailabilitySlot } from './protocol';

export interface DaySlots {
  date: string; // YYYY-MM-DD, in the zone it was grouped for
  slots: AvailabilitySlot[];
}

// One cell of the month grid. `date` is null for the leading/trailing blanks that
// pad the first and last weeks.
export interface DayCell {
  date: string | null;
  day: number;
  slotCount: number;
  selectable: boolean;
}

// A zone name Intl will accept. Intl THROWS on one it doesn't recognise, and
// these arrive over the wire from an upstream calendar, so `safeTimeZone` is the
// only way to obtain one — the brand is what makes skipping it a type error
// rather than a blank pane mid-call.
export type TimeZone = string & { readonly __timeZone: unique symbol };

export function safeTimeZone(id: string | null | undefined, fallback: string): TimeZone {
  for (const candidate of [id, fallback]) {
    if (!candidate) continue;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: candidate });
      return candidate as TimeZone;
    } catch {
      // try the next one
    }
  }
  return 'UTC' as TimeZone;
}

// 'en-CA' formats as YYYY-MM-DD, which sorts lexicographically and matches the
// shape the window uses — so day keys compare as strings without parsing.
function dayKeyFormatter(timeZone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

// Group slots onto the days they fall on in `timeZone`, ascending. A slot whose
// start doesn't parse is dropped: one unreadable row shouldn't cost the SDR the
// rest of the calendar.
export function groupSlotsByDay(slots: AvailabilitySlot[], timeZone: TimeZone): DaySlots[] {
  const format = dayKeyFormatter(timeZone);
  const byDay = new Map<string, AvailabilitySlot[]>();

  for (const slot of slots) {
    const instant = new Date(slot.startUtc);
    if (Number.isNaN(instant.getTime())) continue;
    const key = format.format(instant);
    const existing = byDay.get(key);
    if (existing) existing.push(slot);
    else byDay.set(key, [slot]);
  }

  return [...byDay.entries()]
    .map(([date, daySlots]) => ({
      date,
      slots: [...daySlots].sort((a, b) => a.startUtc.localeCompare(b.startUtc)),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function firstAvailableDay(days: DaySlots[]): string | null {
  return days.find((d) => d.slots.length > 0)?.date ?? null;
}

// The YYYY-MM this day belongs to.
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

const MONTH_PATTERN = /^\d{4}-\d{2}$/;

// The months the window touches, ascending. A reversed or unparseable window
// yields a single month rather than two years of empty grids — paging through
// those reads as the seller having no time, when in truth we never asked.
export function monthsInWindow(window: { start: string; end: string }): string[] {
  const first = monthOf(window.start);
  const last = monthOf(window.end);
  if (!MONTH_PATTERN.test(first) || !MONTH_PATTERN.test(last) || last < first) return [first];
  if (first === last) return [first];

  const months: string[] = [];
  let [year, month] = first.split('-').map(Number) as [number, number];
  for (let guard = 0; guard < 24; guard++) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    months.push(key);
    if (key === last) break;
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

// Build one month's cells, Monday-first, padded to whole weeks. A day is
// selectable only when it has slots — which already excludes the past and
// everything outside the window, since neither can carry any.
export function monthGrid(month: string, days: DaySlots[]): DayCell[] {
  const [year, monthNumber] = month.split('-').map(Number) as [number, number];
  const slotsByDate = new Map(days.map((d) => [d.date, d.slots.length]));

  // Built in UTC and read back with UTC getters: these are calendar coordinates,
  // not instants, so a local-time Date would shift them across a day boundary.
  const firstOfMonth = new Date(Date.UTC(year, monthNumber - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const leading = (firstOfMonth.getUTCDay() + 6) % 7; // Sunday is 0; shift to Monday-first

  const cells: DayCell[] = [];
  for (let i = 0; i < leading; i++) {
    cells.push({ date: null, day: 0, slotCount: 0, selectable: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const date = `${month}-${String(day).padStart(2, '0')}`;
    const slotCount = slotsByDate.get(date) ?? 0;
    cells.push({ date, day, slotCount, selectable: slotCount > 0 });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ date: null, day: 0, slotCount: 0, selectable: false });
  }
  return cells;
}

export interface CalendarView {
  months: string[];
  shownMonth: string;
  monthIndex: number;
  day: string | null;
  cells: DayCell[];
  slots: AvailabilitySlot[];
}

// Derived rather than stored: a timezone change moves slots between days, and a
// fresh calendar can retire the month being viewed.
//
// The months are the union of the window's and the ones the slots actually landed
// in — read a US window in Sydney and its last slots fall into a month the window
// never mentions, which paging would otherwise never reach.
export function calendarView(
  days: DaySlots[],
  window: { start: string; end: string },
  selectedDay: string | null,
  selectedMonth: string | null,
): CalendarView {
  const months = [
    ...new Set([...monthsInWindow(window), ...days.map((d) => monthOf(d.date))]),
  ].sort();
  // Clamped to a month that still exists, so a stale pick can't strand the SDR
  // on a grid with both arrows dead.
  const anyDay = reselectDay(days, selectedDay);
  const shownMonth =
    selectedMonth && months.includes(selectedMonth)
      ? selectedMonth
      : anyDay
        ? monthOf(anyDay)
        : (months[0] ?? '');

  // Chosen from the shown month alone, so paging never leaves one month's grid
  // sitting above another month's times.
  const day = reselectDay(
    days.filter((d) => monthOf(d.date) === shownMonth),
    selectedDay,
  );

  return {
    months,
    shownMonth,
    monthIndex: months.indexOf(shownMonth),
    day,
    cells: shownMonth ? monthGrid(shownMonth, days) : [],
    slots: slotsForDay(days, day),
  };
}

// 12-hour, matching the lead's local time shown a few lines above it on the same
// card. Two clock conventions side by side is a misreading waiting to happen,
// and this is the number the SDR says out loud.
export function formatSlotTime(startUtc: string, timeZone: TimeZone): string {
  const instant = new Date(startUtc);
  if (Number.isNaN(instant.getTime())) return '--:--';
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(instant);
}

// "Mon 25 Aug" — enough to read back to a prospect without the year, which is
// never in doubt across a two-week window. Formatted in UTC because `date` is
// already a calendar coordinate in the zone it was grouped for; re-projecting it
// would convert twice and land a day out past UTC+12.
export function formatDayLabel(date: string): string {
  const instant = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(instant.getTime())) return date;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(instant);
}

// "19–31 Aug" — the honest statement of what we actually know, which the grid
// itself can't make: a greyed-out day looks the same whether the seller is busy
// or we simply never asked.
// Null when we have no window to state — upstream sends none for a client with
// no snapshot loaded. Saying nothing beats printing an empty range.
export function formatWindow(window: { start: string; end: string }): string | null {
  const start = new Date(`${window.start}T12:00:00Z`);
  const end = new Date(`${window.end}T12:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }
  const day = (d: Date): string =>
    new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric' }).format(d);
  const dayMonth = (d: Date): string =>
    new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(d);
  return monthOf(window.start) === monthOf(window.end)
    ? `${day(start)}–${dayMonth(end)}`
    : `${dayMonth(start)} – ${dayMonth(end)}`;
}

export function formatMonthLabel(month: string): string {
  const instant = new Date(`${month}-01T12:00:00Z`);
  if (Number.isNaN(instant.getTime())) return month;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(instant);
}

export interface TimeZoneOption {
  id: TimeZone;
  label: string;
}

// A short list, not the full IANA set: this is picked one-handed during a live
// call, and the sellers are US-based. Arizona is listed apart from Mountain
// because it doesn't observe DST, so for half the year they are different
// clocks — the kind of mistake that only surfaces when someone doesn't show up.
export const TIMEZONE_OPTIONS: TimeZoneOption[] = (
  [
    { id: 'America/New_York', label: 'Eastern (US)' },
    { id: 'America/Chicago', label: 'Central (US)' },
    { id: 'America/Denver', label: 'Mountain (US)' },
    { id: 'America/Phoenix', label: 'Arizona' },
    { id: 'America/Los_Angeles', label: 'Pacific (US)' },
    { id: 'America/Anchorage', label: 'Alaska' },
    { id: 'Pacific/Honolulu', label: 'Hawaii' },
    { id: 'Europe/London', label: 'UK' },
    { id: 'Europe/Berlin', label: 'Central Europe' },
    { id: 'Asia/Dubai', label: 'Gulf' },
    { id: 'Asia/Kolkata', label: 'India' },
    { id: 'Asia/Singapore', label: 'Singapore' },
    { id: 'Australia/Sydney', label: 'Sydney' },
  ] as { id: string; label: string }[]
).map((o) => ({ ...o, id: safeTimeZone(o.id, o.id) }));

// The list with the lead's own zone guaranteed present. Lead zones come from a
// phone's area code and include Canadian ones, so the default is regularly a
// zone this list doesn't carry — without this it would vanish from the dropdown
// the moment the SDR picked anything else.
export function timeZoneOptions(leadTimeZone: string): TimeZoneOption[] {
  const zone = safeTimeZone(leadTimeZone, 'UTC');
  const known = TIMEZONE_OPTIONS.find((o) => o.id === zone);
  return known
    ? [{ ...known, label: `${known.label} — lead` }, ...TIMEZONE_OPTIONS.filter((o) => o !== known)]
    : [{ id: zone, label: `${zone} — lead` }, ...TIMEZONE_OPTIONS];
}

// Which day to show after a timezone change. Keep the SDR where they were if
// that day still has anything on it; otherwise send them to the first day that
// does, rather than leaving them on an empty pane they didn't ask for.
export function reselectDay(days: DaySlots[], selected: string | null): string | null {
  if (selected && days.some((d) => d.date === selected && d.slots.length > 0)) return selected;
  return firstAvailableDay(days);
}

export function slotsForDay(days: DaySlots[], date: string | null): AvailabilitySlot[] {
  if (!date) return [];
  return days.find((d) => d.date === date)?.slots ?? [];
}
